import { AWSError } from 'aws-sdk';

export enum MessageTypes {
    getContentTemplate = 'getContentTemplate',
    getContent = 'getContent',
    getContentPermissions = 'getContentPermissions',
    createContent = 'createContent',
    updateContent = 'updateContent',
    updateContentPermissions = 'updateContentPermissions',
    getParticipantEventCodeInfo = 'getParticipantEventCodeInfo',
    getParticipantEvent = 'getParticipantEvent',
    joinEvent = 'joinEvent',
    general = 'general',
}

export enum ErrorCode {
    InvalidEventCodeException = 'InvalidEventCodeException',
    InvalidEventStateException = 'InvalidEventStateException',
    EventOutOfCapacityException = 'EventOutOfCapacityException',
    NotAuthorizedException = 'NotAuthorizedException',
    // These are custom, client-side errors generated based event code info response
    ExpiredEventException = 'ExpiredEventException',
    EventNotReadyException = 'EventNotReadyException',
    // Generic HTTP errors
    // Internet dies, VPN connection changes, etc.
    NetworkingError = 'NetworkingError',
}

type InferServerErrorMessage<T> = (error: AWSError) => T;

interface Message {
    [key: string]: string | InferServerErrorMessage<string | undefined>;
}
type Messages = {
    [key in MessageTypes]: Message;
};

const messages: Messages = {
    getContentTemplate: {
        '404': 'The requested workshop template no longer exists.',
    },
    getContent: {
        '403': 'You are not authorized to perform this action.',
        '404': 'The requested workshop no longer exists.',
    },
    getContentPermissions: {
        '404': 'The requested workshop no longer exists.',
    },
    createContent: {
        '403': 'You are not authorized to perform this action.',
        '422':
            'The request could not be processed because of an invalid or missing required parameter.',
    },
    updateContent: {
        '403': 'You are not authorized to perform this action.',
        '404': 'The workshop you are trying to update no longer exists.',
    },
    updateContentPermissions: {
        '403': 'You are not authorized to perform this action.',
        '404': 'The workshop you are trying to update no longer exists.',
    },
    getParticipantEventCodeInfo: {
        '400': (error: AWSError) => {
            switch (error.code) {
                case ErrorCode.InvalidEventCodeException:
                    return 'Invalid access code.';
                case ErrorCode.InvalidEventStateException:
                    return 'The event is currently not in a joinable state.';
                case ErrorCode.ExpiredEventException:
                    return 'The event you are trying to join is expired.';
                case ErrorCode.EventNotReadyException:
                    return 'The event is not ready to join.';
                default:
                    return invokeOrReturnValue(messages.general['400'], error);
            }
        },
        '403':
            'You are not allow-listed to join this event. Please reach out to your event facilitator for further assistance.',
    },
    getParticipantEvent: {
        '403': (error: AWSError) => {
            if (error.message === 'error participant is not in a valid state') {
                return 'You have been removed from the event. The event may have ended, or you may have joined another event.';
            }

            return 'You are not authorized to view this event.';
        },
    },
    joinEvent: {
        '400': (error: AWSError) => {
            switch (error.code) {
                case ErrorCode.InvalidEventCodeException:
                    return 'Invalid access code.';
                case ErrorCode.InvalidEventStateException:
                    return 'The event is currently not in a joinable state.';
                case ErrorCode.EventOutOfCapacityException:
                    return 'Event is at capacity. Please reach out to your event facilitator for further assistance.';
                default:
                    return invokeOrReturnValue(messages.general['400'], error);
            }
        },
        '422': (error: AWSError) => {
            switch (error.code) {
                case ErrorCode.EventOutOfCapacityException:
                    return 'Event is at capacity. Please reach out to your event facilitator for further assistance.';
                default:
                    return invokeOrReturnValue(messages.general['422'], error);
            }
        },
        '403':
            'You are not allow-listed to join this event. Please reach out to your event facilitator for further assistance.',
    },
    general: {
        '400':
            'The request you are trying to send is missing data, or contains invalid data.',
        '401': 'Your credentials are not valid.',
        '403': 'You are not authorized to perform this action.',
        '404': 'The information you requested no longer exists.',
        '500': (error: AWSError) => {
            switch (error.code) {
                case ErrorCode.NetworkingError:
                    return 'Could not process the request due to a networking issue. Check for any browser extensions (e.g. Ad blockers and CORS plugins) and active VPN or remote access clients that can modify or block network requests.';
                default:
                    return 'Could not process the request right now because of an issue with the server. Try again later.';
            }
        },
    },
};

/**
 * Typescript guard that will determine if a given
 * argument is a Function
 */
const isFunction = (value: unknown): value is Function => {
    return typeof value === 'function';
};

/**
 * Given a value, if it is a function try to invoke and get
 * appropriate error message, else return string if found.
 */
const invokeOrReturnValue = (
    val: string | InferServerErrorMessage<string | undefined>,
    error: AWSError
): string | undefined => {
    if (isFunction(val)) {
        return val(error);
    }
    return val;
};

class ErrorMessage {
    /**
     * Gets an error message string based on AWSError.statusCode and MessageTypes.
     * Attempts to get error message with specified status code and type. If that does
     * not exist, it will fall back to a general message with the specified status code.
     * If that also does not exist, it will return a general 500 error message.
     * @param error
     * @param type
     */
    getMessage(
        error: AWSError,
        type: keyof typeof messages = MessageTypes.general
    ): string {
        if (!error.statusCode) {
            return invokeOrReturnValue(
                messages.general?.['500'],
                error
            ) as string;
        }
        return (invokeOrReturnValue(
            messages?.[type]?.[error.statusCode],
            error
        ) ||
            invokeOrReturnValue(messages.general?.[error.statusCode], error) ||
            invokeOrReturnValue(messages.general?.['500'], error)) as string;
    }
}

export default new ErrorMessage();
